import { timestampDate } from "@bufbuild/protobuf/wkt";
import { Code, ConnectError, createClient, type Interceptor } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { getAccessToken, setAccessToken } from "./auth-state";
import { ActivityService } from "./types/proto/api/v1/activity_service_pb";
import { AttachmentService } from "./types/proto/api/v1/attachment_service_pb";
import { AuthService } from "./types/proto/api/v1/auth_service_pb";
import { ChecklistService } from "./types/proto/api/v1/checklist_service_pb";
import { IdentityProviderService } from "./types/proto/api/v1/idp_service_pb";
import { InstanceService } from "./types/proto/api/v1/instance_service_pb";
import { JournalService } from "./types/proto/api/v1/memo_service_pb";
import { ShortcutService } from "./types/proto/api/v1/shortcut_service_pb";
import { TagService } from "./types/proto/api/v1/tag_service_pb";
import { TickerService } from "./types/proto/api/v1/ticker_service_pb";
import { UserService } from "./types/proto/api/v1/user_service_pb";
import { ValuationService } from "./types/proto/api/v1/valuation_service_pb";
import { WatchlistService } from "./types/proto/api/v1/watchlist_service_pb";
import { redirectOnAuthFailure } from "./utils/auth-redirect";

// ============================================================================
// Constants
// ============================================================================

const RETRY_HEADER = "X-Retry";
const RETRY_HEADER_VALUE = "true";

// API base URL from environment variable or fallback to current origin
export const API_BASE_URL = import.meta.env.VITE_API_URL || window.location.origin;

// ============================================================================
// Token Refresh State Management
// ============================================================================

const createTokenRefreshManager = () => {
  let isRefreshing = false;
  let refreshPromise: Promise<void> | null = null;

  return {
    async refresh(refreshFn: () => Promise<void>): Promise<void> {
      if (isRefreshing && refreshPromise) {
        return refreshPromise;
      }

      isRefreshing = true;
      refreshPromise = refreshFn().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });

      return refreshPromise;
    },
  };
};

const tokenRefreshManager = createTokenRefreshManager();

// ============================================================================
// Token Refresh
// ============================================================================

const fetchWithCredentials: typeof globalThis.fetch = (input, init) => {
  return globalThis.fetch(input, {
    ...init,
    credentials: "include",
  });
};

// Separate transport without auth interceptor to prevent recursion
const refreshTransport = createConnectTransport({
  baseUrl: API_BASE_URL,
  useBinaryFormat: true,
  fetch: fetchWithCredentials,
  interceptors: [],
});

const refreshAuthClient = createClient(AuthService, refreshTransport);

async function refreshAccessToken(): Promise<void> {
  console.debug("[refreshAccessToken] Starting token refresh...");
  const response = await refreshAuthClient.refreshToken({});

  if (!response.accessToken) {
    throw new ConnectError("Refresh token response missing access token", Code.Internal);
  }

  const expiresAt = response.expiresAt ? timestampDate(response.expiresAt) : undefined;
  setAccessToken(response.accessToken, expiresAt);
  console.debug("[refreshAccessToken] Token refresh successful", {
    hasToken: !!response.accessToken,
    expiresAt: expiresAt?.toISOString(),
  });
}

// Export for App initialization (silent refresh on app load)
// Uses tokenRefreshManager to prevent concurrent refresh requests
export const refreshAccessTokenWithManager = () => tokenRefreshManager.refresh(refreshAccessToken);

// ============================================================================
// Request ID Interceptor (v0.75.0)
// ============================================================================

// Store the most recent request ID for error reporting
let lastRequestID: string | undefined;

/**
 * Get the most recent request ID from the last RPC call
 * Useful for attaching request IDs to error reports
 */
export function getLastRequestID(): string | undefined {
  return lastRequestID;
}

/**
 * Request ID interceptor for tracing
 * Generates or retrieves X-Request-Id header and echoes it back
 */
const requestIDInterceptor: Interceptor = (next) => async (req) => {
  // Generate or retrieve request ID
  let requestID = req.header.get("X-Request-Id");

  if (!requestID) {
    // Generate UUID for new requests
    requestID = crypto.randomUUID();
    req.header.set("X-Request-Id", requestID);
  }

  const startTime = Date.now();

  try {
    const response = await next(req);

    // Extract echoed request ID from response
    const responseRequestID = response.header.get("X-Request-Id");

    // Store for error reporting
    if (responseRequestID) {
      lastRequestID = responseRequestID;
    }

    // Log request completion with timing (development only)
    if (import.meta.env.DEV) {
      console.debug(`[RPC] ${req.method.name} completed in ${Date.now() - startTime}ms`, {
        requestID: responseRequestID || requestID,
        service: req.service.typeName,
        method: req.method.name,
      });
    }

    return response;
  } catch (error) {
    // Store request ID even on error for debugging
    lastRequestID = requestID;

    if (import.meta.env.DEV) {
      // Reduce log noise for unauthenticated errors (expected for anonymous users)
      const isError = error instanceof ConnectError && error.code === Code.Unauthenticated;
      const logMethod = isError ? console.debug : console.error;

      logMethod(`[RPC] ${req.method.name} failed`, {
        requestID,
        service: req.service.typeName,
        method: req.method.name,
        error: error instanceof Error ? error.message : String(error),
        duration: Date.now() - startTime,
      });
    }

    throw error;
  }
};

// ============================================================================
// Authentication Interceptor
// ============================================================================

const authInterceptor: Interceptor = (next) => async (req) => {
  const token = getAccessToken();
  if (token) {
    req.header.set("Authorization", `Bearer ${token}`);
  }

  try {
    return await next(req);
  } catch (error) {
    if (!(error instanceof ConnectError)) {
      throw error;
    }

    if (error.code !== Code.Unauthenticated) {
      throw error;
    }

    if (req.header.get(RETRY_HEADER) === RETRY_HEADER_VALUE) {
      throw error;
    }

    try {
      await tokenRefreshManager.refresh(refreshAccessToken);

      const newToken = getAccessToken();
      if (!newToken) {
        throw new ConnectError("Token refresh succeeded but no token available", Code.Internal);
      }

      req.header.set("Authorization", `Bearer ${newToken}`);
      req.header.set(RETRY_HEADER, RETRY_HEADER_VALUE);
      return await next(req);
    } catch (refreshError) {
      redirectOnAuthFailure();
      throw refreshError;
    }
  }
};

// ============================================================================
// Transport & Service Clients
// ============================================================================

const transport = createConnectTransport({
  baseUrl: API_BASE_URL,
  useBinaryFormat: false,
  fetch: fetchWithCredentials,
  interceptors: [requestIDInterceptor, authInterceptor],
});

// Core service clients
export const instanceServiceClient = createClient(InstanceService, transport);
export const authServiceClient = createClient(AuthService, transport);
export const userServiceClient = createClient(UserService, transport);

// Content service clients
export const journalServiceClient = createClient(JournalService, transport);
export const attachmentServiceClient = createClient(AttachmentService, transport);
export const shortcutServiceClient = createClient(ShortcutService, transport);
export const tagServiceClient = createClient(TagService, transport);
export const activityServiceClient = createClient(ActivityService, transport);
export const checklistServiceClient = createClient(ChecklistService, transport);
export const tickerServiceClient = createClient(TickerService, transport);
export const watchlistServiceClient = createClient(WatchlistService, transport);
export const valuationServiceClient = createClient(ValuationService, transport);

// Configuration service clients
export const identityProviderServiceClient = createClient(IdentityProviderService, transport);
